import $ from 'jquery';
import CurrencyConverter from 'shopify-currency-converter';
import { removeTrapFocus, trapFocus } from '../helpers/a11y';
import Forms from '../Forms';
import VariantHelper from '../VariantHelper';
import AddToCartFlyout from './AddToCartFlyout';
import ProductGallery from './ProductGallery';

export default class ProductDetails {
  constructor(options) {
    this.$window = $(window);
    this.$formArea = options.$formArea;
    this.$gallery = options.$gallery;
    this.$details = options.$details;
    this.context = options.context;
    this.settings = options.settings;
    this.product = options.product;
    this.useHistory = options.useHistory;

    this.variantHelper = null;

    // Product ATC Actions
    this._onATCOpen = this._onATCOpen.bind(this);
    this._onATCClose = this._onATCClose.bind(this);

    this.atcCallbacks = $.extend({
      onClose: this._onATCClose,
      onOpen: this._onATCOpen,
    }, options.atcCallbacks);

    // Form
    this.$form = this.$formArea.find('[data-product-form]');
    this.$productAtcButton = this.$formArea.find('[data-product-atc]');
    this.$productVariants = this.$form.find('[data-variants]');
    this.$productOptions = this.$form.find('[data-product-option]');

    this.variantFields = {
      $price: this.$details.find('[data-price]'),
      $priceMoney: this.$details.find('[data-price] .money'),
      $compareAtPrice: this.$details.find('[data-price-compare-at]'),
      $compareAtPriceMoney: this.$details.find('[data-price-compare-at] .money'),
      $badge: this.$details.find('[data-badge-sales]'),
      $badgeMoneySaved: this.$details.find('[data-price-money-saved]'),
      $badgePercentSaved: this.$details.find('[data-price-percent-saved]'),
      $sku: this.$details.find('[data-product-sku]'),
    };

    this.forms = new Forms(this.$form);
    this.gallery = new ProductGallery(options);

    this._bindEvents();
    this._initOptions();
  }

  unload() {
    this.$form.off('.product-details');
    this.forms.unload();

    if (this.variantHelper) {
      this.variantHelper.unload();
    }

    this.gallery.unload();
  }

  _bindEvents() {
    this.$window.on('product-variant-switch.product-details', (event, data) => this._switchVariant(event, data));
    this.$form.on('submit.product-details', event => this._AddToCartFlyout(event));
  }

  _initOptions() {
    if (!this.$productVariants.length) return;

    this.variantHelper = new VariantHelper(
      this.product,
      this.$productVariants,
      this.$productOptions,
    );
  }

  _switchVariant(event, data) {
    if (data.product !== this.product) return;

    const variant = data.variant;
    const firstLoad = data.firstLoad;

    if (firstLoad) {
      return;
    }

    // Update main select
    this.$productVariants.val(data.variant.id);
    this.gallery.variantChange(variant);

    // Update Variant information
    this._updatePrice(variant);
    this._updateSKU(variant);
    this._updateBadge(variant);
    this._updateButton(variant);

    if (this.useHistory) {
      const url = `${ this.product.handle }?${$.param({ variant: variant.id })}`;
      history.replaceState({}, 'variant', url);
    }
  }

  _updatePrice(variant) {
    // Update compare at price
    const hasComparePrice = (
      !!variant.compare_at_price && variant.compare_at_price > variant.price
    );
    this.variantFields.$compareAtPrice.toggleClass('visible', hasComparePrice);
    this.variantFields.$compareAtPriceMoney.text(Shopify.formatMoney(variant.compare_at_price, this.settings.money_format));
    CurrencyConverter.update(this.variantFields.$compareAtPriceMoney[0]);

    // Update price
    this.variantFields.$priceMoney.text(Shopify.formatMoney(variant.price, this.settings.money_format));
    CurrencyConverter.update(this.variantFields.$priceMoney[0]);
  }

  _updateSKU(variant) {
    if (variant.sku === '') {
      this.variantFields.$sku.parent().addClass('product-sku--empty');
    } else {
      this.variantFields.$sku.parent().removeClass('product-sku--empty');
    }

    this.variantFields.$sku.text(variant.sku);
  }

  _updateBadge(variant) {
    const priceSaved = variant.compare_at_price ? variant.compare_at_price - variant.price : 0;
    this.variantFields.$badge.toggle(!!priceSaved);

    if (this.variantFields.$badgeMoneySaved.length) {
      // Update badge if it shows money saved
      this.variantFields.$badgeMoneySaved.text(Shopify.formatMoney(priceSaved, this.settings.money_format));
      CurrencyConverter.update(this.variantFields.$badgeMoneySaved[0]);
    }

    if (this.variantFields.$badgePercentSaved.length) {
      // Update badge if it shows percentiles
      const percentileSaved = Math.round((priceSaved * 100) / variant.compare_at_price);
      this.variantFields.$badgePercentSaved.text(percentileSaved);
    }
  }

  _updateButton(variant) {
    if (variant.available) {
      this.$productAtcButton.text(this.context.product_available);
      this.$productAtcButton
        .removeClass('disabled')
        .prop('disabled', false);
    } else {
      this.$productAtcButton.text(this.context.product_unavailable);
      this.$productAtcButton
        .addClass('disabled')
        .prop('disabled', true);
    }
  }

  _AddToCartFlyout(event) {
    event.preventDefault();

    const formData = this.$form.serializeArray();

    const options = {
      $atcButton: this.$productAtcButton,
      settings: {
        moneyFormat: this.settings.money_format,
        cartRedirection: this.settings.cart_redirection,
      },
    };

    AddToCartFlyout.init(formData, options, this.atcCallbacks);
  }

  /**
   * Callback when AddToCartFlyout opens
   * Move keyboard focus into flyout
   *
   * @param {object} options - Options to be used
   * @param {jQuery} options.$container - Container to trap focus within
   * @param {jQuery} options.$elementToFocus - Element to be focused when focus leaves container
   * @private
   */
  _onATCOpen(options) {
    trapFocus({
      $container: options.$container,
      $elementToFocus: options.$elementToFocus,
      namespace: 'product-details-flyout',
    });
  }

  /**
   * Callback when AddToCartFlyout closes
   * Move keyboard focus back into grid item
   *
   * @private
   */
  _onATCClose() {
    removeTrapFocus({
      namespace: 'product-details-flyout',
    });

    this.$productAtcButton.focus();
  }
}
